import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formatQuantity } from '@fewerbags/core';
import { api, queryKeys } from '../../lib/api';
import { EmptyState } from '../EmptyState';
import { ChevronRightIcon } from '../icons';
import { PantryIcon } from '../NavIcons';
import { Section, sectionLinkClass } from '../Section';
import { Skeleton } from '../Skeleton';
import { ExpiryBadge } from './ExpiryBadge';

const DAYS = 3;

/** Dashboard card: up to 5 pantry items expiring soonest, with a link into the filtered pantry view. */
export function ExpiringSoonCard() {
  const expiring = useQuery({
    queryKey: queryKeys.pantryExpiring(DAYS),
    queryFn: () => api.pantry.expiring(DAYS),
  });

  const items = expiring.data?.items ?? [];

  return (
    <Section
      id="expiring-soon-heading"
      title="Expiring soon"
      accent="green"
      icon={<PantryIcon className="size-6" />}
      action={
        <Link to={`/pantry?expiringWithinDays=${DAYS}`} className={sectionLinkClass}>
          View pantry <ChevronRightIcon className="size-4" />
        </Link>
      }
    >
      {expiring.isPending ? <Skeleton lines={2} label="Loading the pantry" /> : null}
      {expiring.isError ? (
        <p className="text-sm text-fail">Couldn’t load what’s expiring.</p>
      ) : null}
      {expiring.isSuccess && items.length === 0 ? (
        <EmptyState
          compact
          accent="green"
          icon={<PantryIcon className="size-full" />}
          title="All fresh"
        >
          Nothing expiring in the next {DAYS} days.
        </EmptyState>
      ) : null}
      {items.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line">
          {items.slice(0, 5).map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-3 py-2">
              <span className="truncate text-sm font-medium text-ink">
                {item.ingredientName}{' '}
                <span className="font-normal text-muted">
                  · {formatQuantity(item.qty, item.unit)}
                </span>
              </span>
              <ExpiryBadge daysUntilExpiry={item.daysUntilExpiry} />
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}
